import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { MIN_PLAYERS, type RoomConfig } from '@juegazo/shared';
import { loadLocalConfig, saveLocalConfig } from '../lib/local-config';
import './background';
import './game-config';
import './tutorial-view';
import { findDuplicateIndices } from './players-config';

const STORAGE_LAST_NAME = 'juegazo.lastName';
const STORAGE_LAST_ROOM_CODE = 'juegazo.lastRoomCode';
const STORAGE_LOCAL_PLAYERS = 'juegazo.localPlayers';

const readString = (key: string): string => {
  try {
    return localStorage.getItem(key) ?? '';
  } catch {
    return '';
  }
};

const readStringArray = (key: string): string[] | null => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return null;
    return parsed.filter((n): n is string => typeof n === 'string');
  } catch {
    return null;
  }
};

const writeString = (key: string, value: string) => {
  try {
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  } catch {
    // ignore
  }
};

const writeStringArray = (key: string, value: string[]) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore
  }
};

@customElement('jgz-home')
export class JgzHome extends LitElement {
  createRenderRoot() {
    return this;
  }

  @property({ type: String }) displayName = '';
  /** Error de la última acción de la pantalla de inicio (entrar a una sala, crearla). */
  @property({ type: String }) error: string | null = null;
  @state() private name = readString(STORAGE_LAST_NAME);
  @state() private roomId = readString(STORAGE_LAST_ROOM_CODE);
  @state() private mode: 'menu' | 'create' | 'join' | 'local' = 'menu';
  @state() private tutorialOpen = false;

  willUpdate(changed: Map<string, unknown>) {
    if (changed.has('displayName') && !this.name && this.displayName) {
      this.name = this.displayName;
    }
  }

  private setName = (val: string) => {
    this.name = val;
    writeString(STORAGE_LAST_NAME, val);
    this.clearError();
  };

  private setRoomId = (val: string) => {
    const upper = val.toUpperCase();
    this.roomId = upper;
    writeString(STORAGE_LAST_ROOM_CODE, upper);
    this.clearError();
  };

  /** Avisa al padre para que esconda el error: el jugador ya está corrigiendo. */
  private clearError = () => {
    if (!this.error) return;
    this.dispatchEvent(new CustomEvent('jgz-error-clear', { bubbles: true, composed: true }));
  };

  private pasteCode = async () => {
    try {
      const text = await navigator.clipboard.readText();
      const cleaned = text.replace(/[^a-zA-Z0-9]/g, '').slice(0, 6).toUpperCase();
      this.setRoomId(cleaned);
    } catch {
      // ignore (clipboard permission denied or unavailable)
    }
  };

  private go = (mode: 'create' | 'join' | 'local') => {
    this.mode = mode;
    this.clearError();
  };

  private back = () => {
    this.mode = 'menu';
    this.clearError();
  };

  private submitCreate = () => {
    const name = this.name.trim();
    if (!name) return;
    this.dispatchEvent(
      new CustomEvent('jgz-join', { detail: { mode: 'create', displayName: name }, bubbles: true, composed: true }),
    );
  };

  private submitJoin = () => {
    const name = this.name.trim();
    const roomId = this.roomId.trim().toUpperCase();
    if (!name || !roomId) return;
    this.dispatchEvent(
      new CustomEvent('jgz-join', { detail: { mode: 'join', displayName: name, roomId }, bubbles: true, composed: true }),
    );
  };

  render() {
    // <jgz-background> lives at the top level so Lit reuses the same DOM
    // element across mode changes — stars/confetti/clouds stay put when
    // navigating between menu / form / local config.
    let view: import('lit').TemplateResult;
    if (this.mode === 'menu') view = this.renderMenu();
    else if (this.mode === 'local') view = this.renderLocal();
    else view = this.renderForm();
    return html`
      <jgz-background></jgz-background>
      ${view}
      ${this.renderTutorial()}
    `;
  }

  private renderMenu() {
    return html`
      <div class="container fade-in" style="padding-top: 3rem; position: relative; z-index: 1;">
        <div class="stack" style="gap: 1.25rem; align-items: stretch; max-width: 520px; margin: 0 auto;">
          <div style="text-align: center; margin-bottom: 1rem;">
            <div class="juegazo-logo-wrap">
              <div class="juegazo-logo" style="font-family: 'Bungee', 'Impact', var(--font); line-height: 0.95; font-weight: 900; letter-spacing: 0.02em; font-size: clamp(3rem, 12vw, 4.75rem); transform: rotate(-3deg); transform-origin: center;">
                <span style="color: #FFFFFF;">¡JUEGA</span><span style="background: linear-gradient(135deg, #FDCB6E 0%, #FF7675 100%); -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent; color: transparent;">ZO!</span>
              </div>
            </div>
            <p class="muted" style="margin-top: 1rem; font-size: 1.05rem;">Pasa la bomba antes<br/>de que explote.</p>
          </div>
          <button class="btn-pill primary" @click=${() => this.go('create')}>
            <span class="label">CREAR SALA</span>
            <span class="sublabel">(Inicia una partida multidispositivo)</span>
          </button>
          <button
            class="btn-pill primary"
            style="background: linear-gradient(135deg, var(--teal), var(--teal-2)); color: #0E3A2C; box-shadow: 0 6px 24px rgba(0, 184, 148, 0.42);"
            @click=${() => this.go('join')}
          >
            <span class="label">UNIRSE A SALA</span>
            <span class="sublabel">(Usa el código de una sala)</span>
          </button>
          <button
            class="btn-pill primary"
            style="background: linear-gradient(135deg, var(--purple), var(--purple-2)); color: #fff; box-shadow: 0 6px 24px rgba(108, 92, 231, 0.42);"
            @click=${() => this.go('local')}
          >
            <span class="label">JUEGO LOCAL</span>
            <span class="sublabel">(Todos en el mismo dispositivo)</span>
          </button>
          <button
            class="btn-pill primary"
            style="background: linear-gradient(135deg, var(--yellow), var(--yellow-2)); color: #1F1F38; box-shadow: 0 6px 24px rgba(253, 203, 110, 0.42);"
            @click=${() => (this.tutorialOpen = true)}
            data-testid="open-tutorial"
          >
            <span class="label">CÓMO JUGAR</span>
            <span class="sublabel">(Aprende en 5 pasos)</span>
          </button>
        </div>
      </div>
    `;
  }

  private renderTutorial() {
    if (!this.tutorialOpen) return nothing;
    return html`
      <jgz-tutorial
        ?open=${true}
        @jgz-close=${() => (this.tutorialOpen = false)}
      ></jgz-tutorial>
    `;
  }

  private renderForm() {
    return html`
      <div class="container fade-in" style="max-width: 480px; margin: 0 auto;">
        <div class="stack" style="gap: 1rem;">
          <button class="btn-pill gray" @click=${this.back} style="min-height: 0; padding: 0.6rem 1rem;">
            <span class="label" style="font-size: 1rem;">Volver</span>
          </button>
          <h1 style="font-size: 1.6rem; text-align: center;">${this.mode === 'create' ? 'Crear sala' : 'Unirse a sala'}</h1>
          ${this.error
            ? html`
                <div class="card" role="alert" data-testid="home-error" style="border-color: var(--orange-2);">
                  <p style="margin: 0; color: var(--orange-2);">⚠️ ${this.error}</p>
                </div>
              `
            : nothing}
          <div class="stack" style="gap: 0.6rem;">
            <label class="muted">Tu nombre</label>
            <input
              type="text"
              placeholder="Tu nombre"
              .value=${this.name}
              @input=${(e: Event) => this.setName((e.target as HTMLInputElement).value)}
              maxlength="20"
              autofocus
            />
          </div>
          ${this.mode === 'join'
            ? html`
                <div class="stack" style="gap: 0.6rem;">
                  <label class="muted">Código de sala (6 caracteres)</label>
                  <div class="input-with-actions">
                    <input
                      type="text"
                      placeholder="ABCDEF"
                      .value=${this.roomId}
                      @input=${(e: Event) => this.setRoomId((e.target as HTMLInputElement).value)}
                      maxlength="6"
                      style="letter-spacing: 0.3em; text-transform: uppercase; font-weight: 800; font-size: 1.2rem; padding-right: 4.2rem;"
                    />
                    <div class="input-actions">
                      <button type="button" class="input-action-btn" @click=${this.pasteCode} title="Pegar código" aria-label="Pegar código desde el portapapeles">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1" ry="1"/></svg>
                      </button>
                      ${this.roomId
                        ? html`<button type="button" class="input-action-btn input-clear-btn" @click=${() => this.setRoomId('')} title="Limpiar" aria-label="Limpiar código">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M18 6L6 18M6 6l12 12"/></svg>
                          </button>`
                        : nothing}
                    </div>
                  </div>
                </div>
              `
            : nothing}
          <button
            class="btn-pill primary"
            @click=${this.mode === 'create' ? this.submitCreate : this.submitJoin}
          >
            <span class="label">${this.mode === 'create' ? 'CREAR SALA' : 'UNIRSE'}</span>
          </button>
        </div>
      </div>
    `;
  }

  @state() private localPlayers: string[] = (() => {
    const saved = readStringArray(STORAGE_LOCAL_PLAYERS);
    if (saved && saved.length >= 2) return saved;
    return ['', ''];
  })();
  @state() private localConfig: RoomConfig = loadLocalConfig();

  private persistLocalPlayers = () => {
    writeStringArray(STORAGE_LOCAL_PLAYERS, this.localPlayers);
  };

  private onLocalPlayersChange = (e: CustomEvent<{ players: string[] }>) => {
    if (!e.detail || !Array.isArray(e.detail.players)) return;
    this.localPlayers = [...e.detail.players];
    this.persistLocalPlayers();
  };

  private onLocalConfigChange = (e: CustomEvent<RoomConfig>) => {
    if (!e.detail) return;
    this.localConfig = e.detail;
    saveLocalConfig(e.detail);
  };

  private get trimmedPlayers(): string[] {
    return this.localPlayers.map((p) => p.trim());
  }

  private get duplicateIndices(): Set<number> {
    return findDuplicateIndices(this.localPlayers);
  }

  private get hasDuplicates(): boolean {
    return this.duplicateIndices.size > 0;
  }

  private get canStart(): boolean {
    const filled = this.trimmedPlayers.filter(Boolean);
    return filled.length >= MIN_PLAYERS && !this.hasDuplicates;
  }

  private startLocal = () => {
    const names = this.trimmedPlayers.filter(Boolean);
    if (names.length < MIN_PLAYERS || this.hasDuplicates) return;
    this.dispatchEvent(
      new CustomEvent('jgz-local', {
        detail: {
          displayName: names[0],
          playerNames: names,
          config: this.localConfig,
        },
        bubbles: true,
        composed: true,
      }),
    );
  };

  private renderLocal() {
    const playersOk = this.canStart;
    return html`
      <div class="container fade-in" style="max-width: 560px; margin: 0 auto;">
        <div class="stack" style="gap: 1rem;">
          <button class="btn-pill gray" @click=${this.back} style="min-height: 0; padding: 0.6rem 1rem;">
            <span class="label" style="font-size: 1rem;">Volver</span>
          </button>
          <h1
            style="font-size: 1.6rem; margin: 0; text-align: center; width: 100%; display: block;"
          >
            Juego Local
          </h1>
          <p class="muted-strong" style="text-align: center; width: 100%; display: block; margin: 0;">
            Configura el juego a tu gusto.
          </p>
          <div class="card" style="background: rgba(255,255,255,0.04); padding: 1rem;">
            <div class="stack" style="gap: 1.5rem;">
              <jgz-players-config
                .players=${this.localPlayers}
                @change=${this.onLocalPlayersChange}
              ></jgz-players-config>
              <jgz-game-config
                .config=${this.localConfig}
                @change=${this.onLocalConfigChange}
              ></jgz-game-config>
            </div>
          </div>
          <button
            class="btn-pill primary"
            style="width: 100%;"
            ?disabled=${!playersOk}
            @click=${this.startLocal}
          >
            <span class="label">EMPEZAR</span>
          </button>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'jgz-home': JgzHome;
  }
}
